import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Cancel,
  CloudDownload,
  DeleteForever,
  Edit,
  Image,
  InsertDriveFile,
  Link,
  Lock,
  Save,
  VisibilityOff,
} from '@material-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { isLoaded, useFirestore, useFirestoreConnect } from 'react-redux-firebase';
import { Category, Doc, useAuthentication, useMedia } from '../hooks';
import PageLayout from '../components/organisms/PageLayout';
import AdminMenu from '../components/organisms/AdminFilesMenu';
import { useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import dayjs from 'dayjs';

const Home: React.FC = () => {
  const media = useSelector((state: RootState) => state.firestore?.data?.media);
  const docs: Doc[] = useSelector((state: RootState) => state.firestore?.ordered?.media ?? []);
  const categories = useSelector((state: RootState) => state.firestore?.ordered?.categories ?? []);
  const sortedCategories = [...categories]?.sort((a, b) => a.label.localeCompare(b.label));

  const { updateMedias, deleteMedias, loading } = useMedia();
  const [selectedCategory, setSelectedCategory] = useState<Category>(sortedCategories[0]);
  const [filteredDocs, setFilteredDocs] = useState<Doc[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Doc[]>([]);
  const [open, toggleOpen] = useState(false);
  const [edit, toggleEdit] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [label, setLabel] = useState(selectedCategory?.label ?? '');
  const loaded = isLoaded(media);
  const {
    profile: { email, isAdmin, inactive },
  } = useAuthentication();
  const firestore = useFirestore();

  const handleDownload = useCallback(
    async (doc: Doc) => {
      await firestore.collection('logs').add({
        doc: doc.metadata.name,
        email,
        action: 'download',
        date: dayjs().format('DD/MM/YYYY HH:mm:ss'),
        category: categories?.find((cat) => cat.id === doc.category)?.label ?? '',
      });
      const url = doc.url;
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = function () {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(xhr.response);
        a.download = doc.metadata.name; // Name the file anything you'd like.
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
      };
      xhr.open('GET', url);
      xhr.send();
    },
    [categories, email, firestore]
  );

  const handleView = useCallback(
    async (doc: any) => {
      await firestore.collection('logs').add({
        doc: doc.metadata.name,
        email,
        action: 'view',
        date: dayjs().format('DD/MM/YYYY HH:mm:ss'),
        category: categories?.find((cat) => cat.id === doc.category)?.label ?? '',
      });
      window.open(doc.url);
    },
    [categories, email, firestore]
  );

  const isImage = useCallback((doc: Doc) => doc.metadata.contentType.indexOf('image/') === 0, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedDocs.length === filteredDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(filteredDocs);
    }
  }, [filteredDocs, selectedDocs.length]);

  const handleDeleteCategory = useCallback(async () => {
    if (selectedCategory?.id) {
      await deleteMedias({ selectedDocs: docs.filter((doc) => doc.category === selectedCategory.id) });
      await firestore.collection('categories').doc(selectedCategory.id).delete();
      setSelectedCategory(sortedCategories[0]);
      toggleOpen(false);
    }
  }, [selectedCategory?.id, deleteMedias, docs, firestore, sortedCategories]);

  const handleUpdateCategory = useCallback(async () => {
    if (selectedCategory?.id) {
      try {
        await firestore.collection('categories').doc(selectedCategory.id).set({ label });
        setSelectedCategory({ ...selectedCategory, label });
      } catch (e) {
        console.error(e);
      }
      toggleEdit(false);
    }
  }, [firestore, label, selectedCategory]);

  useEffect(() => {
    if (loaded) {
      setFilteredDocs(
        docs.filter(
          (doc) =>
            doc.category === selectedCategory?.id &&
            doc.metadata.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1 &&
            ( isAdmin || !doc.hidden )
        )
      );
    }
  }, [docs, isAdmin, loaded, selectedCategory, searchText]);

  useEffect(() => setSelectedDocs([]), [selectedCategory, filteredDocs]);
  useEffect(
    () => setSelectedCategory((selectedCategory) => categories?.find((cat) => cat.id === selectedCategory?.id)),
    [categories]
  );

  useEffect(() => setLabel(selectedCategory?.label), [selectedCategory]);

  useFirestoreConnect({ collection: 'media' });
  useFirestoreConnect({ collection: 'categories' });

  if (inactive) {
    return (
      <PageLayout>
        <Box padding={3}>
          <Typography variant="h2" align="center">
            <Lock fontSize="large"/>
          </Typography>
          <Typography variant="h3" align="center">
            Il tuo account risulta sospeso.
          </Typography>
          <Typography variant="h4" align="center">
            Contatta l'amministrazione per ottenere supporto.
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {isAdmin && <AdminMenu onSearch={setSearchText} selectedDocs={selectedDocs}/>}
      {( !loaded || loading ) && <LinearProgress/>}
      <Dialog open={open} onClose={() => toggleOpen(false)}>
        <DialogTitle>Attenzione!</DialogTitle>
        <Box padding={3} paddingTop={0}>
          Sei sicuro di voler eliminare la categoria e tutti gli elementi contenuti?
          <br/>
          L'azione è irreversibile.
        </Box>
        <DialogActions>
          <Button onClick={() => toggleOpen(false)} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleDeleteCategory}
            disabled={loading}
            variant="contained"
            color="primary"
            disableElevation
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Divider style={{ margin: '20px 0' }}/>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          {Array.from(categories)
            .sort((a, b) => a.label.localeCompare(b.label))
            .filter((category) => docs.some((doc) => doc.category === category.id && ( isAdmin || !doc.hidden )))
            .map((category) => (
              <Badge badgeContent={docs.filter((doc) => doc.category === category.id && ( isAdmin || !doc.hidden )).length} style={{ width: '100%'}} color="primary">
                <Button
                  variant='contained'
                  color="primary"
                  disableElevation
                  fullWidth
                  key={category.id}
                  onClick={() => setSelectedCategory(category)}
                  style={{ marginBottom: 20, width: '100%', opacity: selectedCategory?.id === category.id ? 1 : 0.8 }}
                >
                  <Box display="flex" justifyContent="space-between" width="100%" textAlign="left">
                    {category.label}
                  </Box>
                </Button>
              </Badge>
            ))}
        </Grid>
        <Grid item xs={9} style={{ maxHeight: 500, overflowY: 'auto' }}>
          <Box
            height="90px"
            position="relative"
            marginLeft={2}
            display="flex"
            alignItems="center"
            style={{ background: '#e5e5e5' }}
          >
            <Box
              position="absolute"
              style={{
                backgroundImage: `url(${selectedCategory?.thumb})`,
                backgroundPosition: 'center right',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
              }}
              width="100%"
              height="100%"
            />
            <Box display="flex" alignItems="center" padding={2}>
              {edit ? (
                <TextField
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Box display="flex">
                        <IconButton onClick={handleUpdateCategory}>
                          <Save/>
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            toggleEdit(false);
                            setLabel(selectedCategory.label);
                          }}
                        >
                          <Cancel/>
                        </IconButton>
                      </Box>
                    ),
                  }}
                />
              ) : (
                <Typography variant="h5" style={{ marginRight: 5 }}>
                  {selectedCategory?.label}
                </Typography>
              )}
              {isAdmin && (
                <>
                  {!edit && (
                    <IconButton onClick={() => toggleEdit(true)}>
                      <Edit/>
                    </IconButton>
                  )}
                  <IconButton onClick={() => toggleOpen(true)}>
                    <DeleteForever/>
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          <Box display="flex" justifyContent="end" p={2} paddingBottom={0} alignItems="center">
            Seleziona tutti
            <Checkbox
              color="primary"
              checked={selectedDocs.length === filteredDocs.length}
              onChange={toggleSelectAll}
            />
          </Box>
          <List>
            {filteredDocs.slice(0, 20).map((doc) => (
              <div key={doc.id} style={{ backgroundColor: doc.hidden ? '#d7d7d7' : 'transparent' }}>
                <ListItem>
                  <ListItemAvatar>
                    {isImage(doc) ? (
                      <Avatar variant="square" style={{ width: 50, height: 50, marginRight: 20 }}>
                        <Image style={{ width: 30, height: 30 }}/>
                      </Avatar>
                    ) : (
                      <Avatar variant="square" style={{ width: 50, height: 50, marginRight: 20 }}>
                        <InsertDriveFile style={{ width: 30, height: 30 }}/>
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    style={{ paddingRight: 110 }}
                    primary={`${doc.metadata.name} ${doc.hidden ? '(Nascosto)' : ''}`}
                    secondary={dayjs(doc.metadata.timeCreated).format('DD/MM/YYYY HH:mm:ss')}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleDownload(doc)}>
                      <CloudDownload/>
                    </IconButton>
                    <IconButton onClick={() => handleView(doc)}>
                      <Link/>
                    </IconButton>
                    {isAdmin && (
                      <>
                        |
                        {doc.hidden && (
                          <IconButton onClick={() => updateMedias({ selectedDocs: [{ ...doc, hidden: false }] })}>
                            <VisibilityOff/>
                          </IconButton>
                        )}
                        <Checkbox
                          color="primary"
                          checked={!!selectedDocs.find(({ id }) => id === doc.id)}
                          onChange={(_e, checked) => {
                            if (checked) setSelectedDocs([...selectedDocs, doc]);
                            else setSelectedDocs(selectedDocs.filter(({ id }) => id !== doc.id));
                          }}
                        />
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider variant="inset" component="li"/>
              </div>
            ))}
          </List>
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default Home;
